import {useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner, Table,  Tabs, Tab} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import axios from 'axios'
import {defineMessages, MessageDescriptor, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {UPDATE_BOTMAKER_TEMPLATE_CONFIG} from '../../gql/mutations/botmaker'
import {GET_BOTMAKER_VARIABLE_OPTIONS} from '../../gql/queries/botmaker'

type WhatsAppTemplate = {
  id?: string
  name?: string
  body?: {text?: string}
  allowedModules?: string[]
  variableMapping?: Record<string, string>
  contentSid?: string
  llmInvocable?: boolean
  llmDescription?: string
  llmTags?: string[]
}

interface ITemplateConfigurationModalProps {
  show: boolean
  onHide: () => void
  template: WhatsAppTemplate
  onSuccess: () => void
  isTwilio?: boolean
}

const M = defineMessages({
  title: {
    id: 'integrations.templateCfg.title',
    defaultMessage: 'Configure template: {name}',
  },
  allowedModules: {
    id: 'integrations.templateCfg.allowedModules',
    defaultMessage: 'Allowed modules',
  },
  allowedModulesHint: {
    id: 'integrations.templateCfg.allowedModulesHint',
    defaultMessage: 'Select where this template can be used.',
  },
  mapping: {id: 'integrations.templateCfg.mapping', defaultMessage: 'Variable mapping'},
  mappingHint: {
    id: 'integrations.templateCfg.mappingHint',
    defaultMessage:
      'Map the template variables (e.g. {example}) to system fields for each allowed module.',
  },
  noVariables: {
    id: 'integrations.templateCfg.noVariables',
    defaultMessage: 'No variables found in the template body.',
  },
  selectModuleFirst: {
    id: 'integrations.templateCfg.selectModuleFirst',
    defaultMessage: 'Please select at least one module to configure mappings.',
  },
  tabLlm: {id: 'integrations.templateCfg.tab.llm', defaultMessage: 'AI / LLM settings'},
  llmInvocable: {
    id: 'integrations.templateCfg.llmInvocable',
    defaultMessage: 'Allow AI/LLM agents to invoke this template autonomously',
  },
  llmDescription: {
    id: 'integrations.templateCfg.llmDescription',
    defaultMessage: 'Description for AI context',
  },
  llmDescriptionPlaceholder: {
    id: 'integrations.templateCfg.llmDescriptionPlaceholder',
    defaultMessage: 'E.g. Use this to notify the customer that their policy is about to expire.',
  },
  llmTags: {id: 'integrations.templateCfg.llmTags', defaultMessage: 'Keywords / tags'},
  llmTagsPlaceholder: {
    id: 'integrations.templateCfg.llmTagsPlaceholder',
    defaultMessage: 'E.g. renewal, payment, reminder',
  },
  colVariable: {id: 'integrations.templateCfg.col.variable', defaultMessage: 'Variable'},
  colSystemField: {
    id: 'integrations.templateCfg.col.systemField',
    defaultMessage: 'System field ({module})',
  },
  modulePolicies: {
    id: 'integrations.templateCfg.module.policies',
    defaultMessage: 'Policies/Quotes',
  },
  toastOptionsError: {
    id: 'integrations.templateCfg.toast.optionsError',
    defaultMessage: 'Error fetching variable options',
  },
  toastSaved: {
    id: 'integrations.templateCfg.toast.saved',
    defaultMessage: 'Configuration saved',
  },
  toastSaveError: {
    id: 'integrations.templateCfg.toast.saveError',
    defaultMessage: 'Error saving configuration',
  },

  // Reusadas
  moduleLeads: {id: 'MENU.item.leads', defaultMessage: 'Leads'},
  moduleContacts: {id: 'MENU.item.contacts', defaultMessage: 'Contacts'},
  selectOption: {id: 'common.placeholder.selectOption', defaultMessage: 'Select an option'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
})

// El ejemplo de sintaxis lleva llaves: viaja como valor del mensaje para que ICU
// no las lea como un placeholder (§17.21).
const VARIABLE_EXAMPLES = '{{1}}, ${name}...'

// i18n-ok: los `value` viajan al backend en allowedModules; la etiqueta visible
// sale de MODULE_LABELS al renderizar (§17.10).
const MODULE_OPTIONS = [{value: 'leads'}, {value: 'policies'}, {value: 'contacts'}]

const MODULE_LABELS: Record<string, MessageDescriptor> = {
  leads: M.moduleLeads,
  policies: M.modulePolicies,
  contacts: M.moduleContacts,
}

export const TemplateConfigurationModal = ({
  show,
  onHide,
  template,
  onSuccess,
  isTwilio = false,
}: ITemplateConfigurationModalProps) => {
  const intl = useIntl()
  const moduleLabel = (mod: string) =>
    MODULE_LABELS[mod] ? intl.formatMessage(MODULE_LABELS[mod]) : mod
  const [loading, setLoading] = useState(false)
  // const [saving, setSaving] = useState(false)
  
  // State
  const [selectedModules, setSelectedModules] = useState<string[]>([])
  // Mapping can be flat (legacy) or nested: {'var': 'field'} OR {'leads': {'var': 'field'}, ...}
  // We'll manage it as `any` locally for flexibility, but try to normalize to module-nested if multiple modules are checked.
  const [mapping, setMapping] = useState<any>({})
  
  // LLM State
  const [llmInvocable, setLlmInvocable] = useState(false)
  const [llmDescription, setLlmDescription] = useState('')
  const [llmTags, setLlmTags] = useState<string>('')

  const [activeTab, setActiveTab] = useState<string>('llm')
  
  const [variableOptions, setVariableOptions] = useState<Record<string, {label: string, variables: {value: string, label: string}[]}>>({})
  const [templateVariables, setTemplateVariables] = useState<string[]>([])

  useEffect(() => {
    if (show && template) {
      // Init state from template
      setSelectedModules(template.allowedModules || [])
      setMapping(template.variableMapping || {})
      if (template.allowedModules && template.allowedModules.length > 0) {
          setActiveTab(template.allowedModules[0])
      } else if (isTwilio) {
          setActiveTab('llm')
      } else {
          setActiveTab('leads')
      }
      
      setLlmInvocable(template.llmInvocable || false)
      setLlmDescription(template.llmDescription || '')
      setLlmTags((template.llmTags || []).join(', '))
      
      
      // Parse variables from body
      let text = ''
      try {
        if (typeof template.body === 'string') {
           const parsedBody = JSON.parse(template.body)
           text = parsedBody.text || ''
        } else if (typeof template.body === 'object' && template.body !== null) {
           text = (template.body as any).text || ''
        }
      } catch (e) {
        console.error("Error parsing template body", e)
      }
      
      // Support both {{var}} and ${var} patterns
      // WhatsApp standard is usually {{1}}, Botmaker often uses ${name}
      // Pattern 1: {{content}}
      const regexDouble = /{{(.*?)}}/g;
      // Pattern 2: ${content}
      const regexDollar = /\${(.*?)}/g;
      
      const found: string[] = []
      let match;
      
      // Check {{...}}
      while ((match = regexDouble.exec(text)) !== null) {
          if(!found.includes(match[1])) {
            found.push(match[1].trim())
          }
      }
      // Check ${...}
      while ((match = regexDollar.exec(text)) !== null) {
          if(!found.includes(match[1])) {
            found.push(match[1].trim())
          }
      }
      setTemplateVariables(found)
      
      // Fetch options
      // fetchOptions() // No longer needed, useQuery handles it
    }
  }, [show, template])

  const {data: optionsData, loading: optionsLoading, error: optionsError} = useQuery(GET_BOTMAKER_VARIABLE_OPTIONS)

  useEffect(() => {
     if (optionsData?.botmakerVariableOptions) {
         const newOptions: Record<string, any> = {}
         optionsData.botmakerVariableOptions.forEach((item: any) => {
             newOptions[item.key] = {
                 label: item.label,
                 variables: item.variables
             }
         })
         setVariableOptions(newOptions)
     }
     if (optionsError) {
        console.error(optionsError)
        toast.error(intl.formatMessage(M.toastOptionsError))
     }
  }, [optionsData, optionsError])

  // Use mutation import string, assuming UPDATE_TWILIO_TEMPLATE_CONFIG is exported 
  // from '../../gql/mutations/twilio'
  const [updateBotmaker] = useMutation(UPDATE_BOTMAKER_TEMPLATE_CONFIG)
  const [updateTwilio] = useMutation(require('../../gql/mutations/twilio').UPDATE_TWILIO_TEMPLATE_CONFIG)

  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!isTwilio && !template.id) return
    if (isTwilio && !template.contentSid) return

    setSaving(true)
    try {
      if (isTwilio) {
        const {data} = await updateTwilio({
          variables: {
            contentSid: template.contentSid,
            allowedModules: selectedModules,
            variableMapping: mapping,
            llmInvocable,
            llmDescription,
            llmTags: llmTags.split(',').map(t => t.trim()).filter(Boolean)
          }
        })
        if (data?.updateTwilioTemplateConfig?.success) {
          toast.success(intl.formatMessage(M.toastSaved))
          onSuccess()
          onHide()
        } else {
           toast.error(
             data?.updateTwilioTemplateConfig?.message || intl.formatMessage(M.toastSaveError)
           )
        }
      } else {
        const {data} = await updateBotmaker({
          variables: {
            pTemplateId: template.id,
            allowedModules: selectedModules,
            variableMapping: mapping
          }
        })
        if (data?.updateBotmakerTemplateConfig?.success) {
          toast.success(intl.formatMessage(M.toastSaved))
          onSuccess()
          onHide()
        } else {
           toast.error(
             data?.updateBotmakerTemplateConfig?.message || intl.formatMessage(M.toastSaveError)
           )
        }
      }

    } catch (e: any) {
      console.error(e)
      toast.error(e.message || intl.formatMessage(M.toastSaveError))
    } finally {
      setSaving(false)
    }
  }

  const toggleModule = (mod: string) => {
    if (selectedModules.includes(mod)) {
      setSelectedModules(selectedModules.filter(m => m !== mod))
    } else {
      setSelectedModules([...selectedModules, mod])
      if (!activeTab) setActiveTab(mod)
    }
  }

  // Helper to get/set mapping for current module (or flat)
  const getValueForVar = (v: string, moduleKey: string) => {
      // If mapping has module key, look inside
      if (mapping[moduleKey] && typeof mapping[moduleKey] === 'object') {
          return mapping[moduleKey][v] || ''
      }
      // Fallback: check if mapping is flat and applied (legacy)
      // BUT if we are in multi-module mode, we should favor nested.
      // If flat exists and we are moving to nested, we might want to migrate it?
      // For simplicity:
      if (typeof mapping[v] === 'string') return mapping[v] // Legacy flat
      
      return ''
  }

  const updateMapping = (v: string, val: string, moduleKey: string) => {
      setMapping((prev: any) => {
          const newMap = {...prev}
          
          // Ensure nested structure for this module
          if (!newMap[moduleKey] || typeof newMap[moduleKey] !== 'object') {
             // If it was mixing flat, we might lose flat data if we don't migrate.
             // Let's copy flat data to this module if it matches? 
             // Or just init empty
             newMap[moduleKey] = {}
          }
           newMap[moduleKey] = {
               ...newMap[moduleKey],
               [v]: val
           }
           return newMap
      })
  }

  return (
    <Modal show={show} onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title, {name: template.name})}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className='text-center py-4'>
            <Spinner animation='border' />
          </div>
        ) : (
          <div>
            {/* Modules Section */}
            <div className='mb-4'>
              <h5 className='fw-bold'>{intl.formatMessage(M.allowedModules)}</h5>
              <div className='text-muted small mb-2'>
                {intl.formatMessage(M.allowedModulesHint)}
              </div>
              <div className='d-flex gap-3'>
                {MODULE_OPTIONS.map(opt => (
                  <Form.Check
                    key={opt.value}
                    type='checkbox'
                    id={`mod-${opt.value}`}
                    label={moduleLabel(opt.value)}
                    checked={selectedModules.includes(opt.value)}
                    onChange={() => toggleModule(opt.value)}
                  />
                ))}
              </div>
            </div>
            
            <hr />

            {/* Variables Section */}
            <div className='mb-3'>
              <h5 className='fw-bold'>{intl.formatMessage(M.mapping)}</h5>
              <div className='text-muted small mb-3'>
                {intl.formatMessage(M.mappingHint, {example: VARIABLE_EXAMPLES})}
              </div>
              
              {templateVariables.length === 0 ? (
                <div className='alert alert-info'>{intl.formatMessage(M.noVariables)}</div>
              ) : selectedModules.length === 0 ? (
                 <div className='alert alert-warning'>
                    {intl.formatMessage(M.selectModuleFirst)}
                 </div>
              ) : (
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || '')}
                    className="mb-3"
                >
                    {isTwilio && (
                      <Tab eventKey="llm" title={intl.formatMessage(M.tabLlm)}>
                        <div className="p-3 border rounded mb-3 bg-light">
                          <Form.Check
                              type="switch"
                              id="custom-switch"
                              label={intl.formatMessage(M.llmInvocable)}
                              checked={llmInvocable}
                              onChange={(e) => setLlmInvocable(e.target.checked)}
                              className="mb-3 fw-bold"
                          />
                          <Form.Group className="mb-3">
                              <Form.Label>{intl.formatMessage(M.llmDescription)}</Form.Label>
                              <Form.Control
                                as="textarea" 
                                rows={2}
                                value={llmDescription} 
                                onChange={e => setLlmDescription(e.target.value)}
                                placeholder={intl.formatMessage(M.llmDescriptionPlaceholder)}
                                disabled={!llmInvocable}
                              />
                          </Form.Group>
                          <Form.Group>
                              <Form.Label>{intl.formatMessage(M.llmTags)}</Form.Label>
                              <Form.Control
                                type="text"
                                value={llmTags} 
                                onChange={e => setLlmTags(e.target.value)}
                                placeholder={intl.formatMessage(M.llmTagsPlaceholder)}
                                disabled={!llmInvocable}
                              />
                          </Form.Group>
                        </div>
                      </Tab>
                    )}
                    {selectedModules.map(mod => {
                        return (
                            <Tab eventKey={mod} title={moduleLabel(mod)} key={mod}>
                                <Table bordered hover size="sm">
                                  <thead>
                                    <tr className='bg-light'>
                                      <th style={{width: '20%'}}>
                                        {intl.formatMessage(M.colVariable)}
                                      </th>
                                      <th>
                                        {intl.formatMessage(M.colSystemField, {
                                          module: moduleLabel(mod),
                                        })}
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {templateVariables.map(v => (
                                      <tr key={v}>
                                        <td className='align-middle fw-bold text-center'>
                                          {v}
                                        </td>
                                        <td>
                                          <Form.Select
                                            value={getValueForVar(v, mod)}
                                            onChange={(e) => updateMapping(v, e.target.value, mod)}
                                          >
                                            <option value="">
                                              {intl.formatMessage(M.selectOption)}
                                            </option>
                                            {Object.entries(variableOptions)
                                                .filter(([groupKey]) => groupKey === mod) // Filter by current module ('leads', 'policies'...)
                                                .map(([groupKey, group]) => {
                                                return (
                                                    <optgroup key={groupKey} label={group.label}>
                                                        {group.variables.map((opt: any) => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                )
                                            })}
                                          </Form.Select>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                            </Tab>
                        )
                    })}
                </Tabs>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onHide} disabled={saving}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button variant='primary' onClick={handleSave} disabled={saving || loading}>
          {saving && <Spinner animation='border' size='sm' className='me-2' />}
          {intl.formatMessage(M.saveChanges)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
